import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { SettingsService, SettingType, SettingTypeAuditRow } from '../../core/settings/settings.service';
import { diffSettingTypes } from '../setting-type-diff.util';

interface AuditFieldChange {
  label: string;
  previousValue: string;
  value: string;
}

// An audit row plus the precomputed diff the grid renders.
interface AuditGridRow extends SettingTypeAuditRow {
  isCreate: boolean;
  changes: AuditFieldChange[];
}

@Component({
  selector: 'settings-types-audit',
  templateUrl: './settings-types-audit.component.html',
  styleUrls: ['./settings-types-audit.component.scss']
})
export class SettingsTypesAuditComponent implements OnInit {
  settingName = '';
  private gridApi!: GridApi<AuditGridRow>;

  columnDefs: ColDef[] = [
    {
      headerName: 'When',
      field: 'appliedDate',
      flex: 1,
      minWidth: 170,
      sort: 'desc',
      valueFormatter: params => (params.value ? new Date(params.value).toLocaleString() : '')
    },
    { headerName: 'Environment', field: 'environment', flex: 1, minWidth: 110 },
    { headerName: 'Action', field: 'action', flex: 1, minWidth: 100 },
    { headerName: 'Changed By', field: 'changedBy', flex: 1, minWidth: 130, tooltipField: 'changedByEmail' },
    {
      headerName: 'Approved By',
      field: 'approvedBy',
      flex: 1,
      minWidth: 130,
      tooltipField: 'approvedByEmail',
      valueFormatter: params => params.value || '—'
    },
    {
      headerName: 'Changes',
      flex: 3,
      minWidth: 260,
      autoHeight: true,
      cellRenderer: this.changesCellRenderer.bind(this)
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly settingsService: SettingsService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.settingName = params.get('name') ?? '';
      // If the grid is already up (param change without re-init), reload for the new setting.
      if (this.gridApi) {
        this.loadAudit();
      }
    });
  }

  async onGridReady(params: GridReadyEvent<AuditGridRow>): Promise<void> {
    this.gridApi = params.api;
    await this.loadAudit();
    window.addEventListener('resize', () => setTimeout(() => params.api.sizeColumnsToFit()));
    this.gridApi.sizeColumnsToFit();
  }

  private async loadAudit(): Promise<void> {
    if (!this.settingName) {
      return;
    }

    this.gridApi?.showLoadingOverlay();
    try {
      const rows = await this.settingsService.getSettingTypeAudit(this.settingName);
      const gridRows = (rows ?? []).map(row => this.toGridRow(row));

      if (gridRows.length === 0) {
        this.gridApi?.setRowData([]);
        this.gridApi?.showNoRowsOverlay();
      } else {
        this.gridApi?.setRowData(gridRows);
        this.gridApi?.hideOverlay();
      }
      this.gridApi?.sizeColumnsToFit();
    } catch (error) {
      console.error('Error loading setting type audit:', error);
      this.toastr.error('Failed to load audit history.');
      this.gridApi?.setRowData([]);
      this.gridApi?.showNoRowsOverlay();
    }
  }

  /**
   * Turns a raw audit row into a grid row, precomputing the field-level diff from the before/after
   * definition snapshots. A row with no "before" is a create (the whole setting is new).
   */
  private toGridRow(row: SettingTypeAuditRow): AuditGridRow {
    const after = this.parseDefinition(row.afterDefinition);
    const before = this.parseDefinition(row.beforeDefinition);
    const isCreate = !before;

    const changes: AuditFieldChange[] = after
      ? diffSettingTypes(after, before ?? undefined)
          .filter(field => field.changed)
          .map(field => ({ label: field.label, previousValue: field.previousValue ?? '', value: field.value }))
      : [];

    return { ...row, isCreate, changes };
  }

  private parseDefinition(json?: string): SettingType | undefined {
    if (!json) {
      return undefined;
    }
    try {
      return JSON.parse(json) as SettingType;
    } catch {
      return undefined;
    }
  }

  private changesCellRenderer(params: { data: AuditGridRow }): HTMLElement {
    const row = params.data;
    const container = document.createElement('div');
    container.className = 'audit-changes';

    if (row.isCreate) {
      container.appendChild(this.textSpan('audit-created', 'Created'));
      return container;
    }

    if (!row.changes || row.changes.length === 0) {
      container.appendChild(this.textSpan('audit-no-change', 'No field changes'));
      return container;
    }

    row.changes.forEach(change => {
      const line = document.createElement('div');
      line.className = 'audit-change-line';
      line.appendChild(this.textSpan('field-label', `${change.label}: `));
      line.appendChild(this.textSpan('old-value', change.previousValue || '(empty)'));
      line.appendChild(this.textSpan('diff-arrow', ' → '));
      line.appendChild(this.textSpan('new-value', change.value || '(empty)'));
      container.appendChild(line);
    });

    return container;
  }

  private textSpan(className: string, text: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  }
}
